import type { Investigation } from '@litlingo/client';
import LoadingIndicator from 'components/LoadingIndicator';
import useClickOutside from 'components/utils/useClickOutside';
import { SEARCH_ICON } from 'constants/filterIcons';
import useInvestigations from 'hooks/investigations/useInvestigations';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PAGE_SIZE = 25;

type InvestigationSelectProps = {
  value: string;
  onChange: (investigationUuid: string) => void;
  disabled?: boolean;
  enabled?: boolean;
  dataTestid?: string;
};

const InvestigationSelect: React.FC<InvestigationSelectProps> = ({
  value,
  onChange,
  disabled = false,
  enabled = true,
  dataTestid = 'add-to-investigation-select',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [records, setRecords] = useState<Investigation[]>([]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const handleClickOutside = useCallback(() => setIsOpen(false), []);
  useClickOutside(wrapperRef, handleClickOutside);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!enabled) {
      setSearch('');
      setDebouncedSearch('');
      setOffset(0);
      setRecords([]);
      setIsOpen(false);
    }
  }, [enabled]);

  const { data, isLoading, isFetching } = useInvestigations({
    enabled,
    queryParamsOverride: {
      limit: String(PAGE_SIZE),
      offset: String(offset),
      order_by: 'updated_at',
      order_desc: 'true',
      broad_search: debouncedSearch,
    },
  });

  useEffect(() => {
    if (!data?.records) {
      return;
    }

    setRecords((prev) => {
      if (offset === 0) {
        return data.records;
      }

      const existing = new Set(prev.map((record) => record.uuid));
      return [...prev, ...data.records.filter((record) => !existing.has(record.uuid))];
    });
  }, [data?.records, offset]);

  const selectedInvestigation = useMemo(
    () => records.find((investigation) => investigation.uuid === value),
    [records, value]
  );

  const totalCount = data?.count ?? records.length;
  const hasMore = records.length < totalCount;
  const loadingList = isLoading || isFetching;

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    if (!disabled) {
      setIsOpen((open) => !open);
    }
  };

  const handleSelect = (investigationUuid: string): void => {
    onChange(investigationUuid);
    setIsOpen(false);
  };

  const handleLoadMore = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setOffset((currentOffset) => currentOffset + PAGE_SIZE);
  };

  const renderInvestigationOptions = (): JSX.Element => {
    if (loadingList && records.length === 0) {
      return (
        <div className="p-2">
          <LoadingIndicator size="5" />
        </div>
      );
    }

    if (records.length === 0) {
      return <div className="p-2 text-sm text-litlingo-gray-5">No investigations found</div>;
    }

    return (
      <>
        {records.map((investigation) => (
          <button
            key={investigation.uuid}
            type="button"
            className={`w-full text-left border-litlingo-gray-2 p-2 focus:outline-none hover:bg-litlingo-gray-1 ${
              investigation.uuid === value ? 'font-bold cursor-default' : 'cursor-pointer'
            }`}
            data-testid={`${dataTestid}-${investigation.name}-option`}
            onClick={(): void => handleSelect(investigation.uuid)}
          >
            {investigation.name}
          </button>
        ))}
      </>
    );
  };

  return (
    <div ref={wrapperRef} className="relative w-full rounded">
      <button
        type="button"
        className={`flex h-8 w-full justify-between items-center px-2 border border-litlingo-gray-2 rounded focus:outline-none ${
          disabled ? 'bg-litlingo-gray-0.5 cursor-default' : ''
        }`}
        data-testid={dataTestid}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className={`truncate text-body ${!selectedInvestigation ? 'italic' : ''}`}>
          {loadingList && !selectedInvestigation
            ? 'Loading…'
            : selectedInvestigation?.name ?? 'Select an investigation'}
        </span>
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="flex-none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.49026 8.44392L1.83398 3L0 4.91123L7.49035 12.0926L15 4.91123L13.166 3L7.49026 8.44392Z"
            fill="currentColor"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          data-testid="investigation-select-dropdown"
          className="origin-bottom-left absolute mt-1 w-full rounded shadow-lg z-[1001] border border-litlingo-gray-2 bg-white"
        >
          <div className="flex flex-row items-center w-full h-8 px-2 border-b border-litlingo-gray-2">
            <div className="w-5 flex-none">{SEARCH_ICON()}</div>
            <input
              type="text"
              value={search}
              onChange={(event): void => setSearch(event.target.value)}
              placeholder="Search investigations"
              className="w-full h-full ml-1 text-body text-litlingo-gray-6 placeholder-italic placeholder-litlingo-gray-6 focus:outline-none"
              data-testid={`${dataTestid}-search`}
              autoComplete="off"
            />
          </div>

          <div className="max-h-60 overflow-auto custom-scrollbar">
            {renderInvestigationOptions()}

            {hasMore && (
              <button
                type="button"
                className="w-full py-2 text-sm border-t border-litlingo-gray-2 focus:outline-none"
                data-testid={`${dataTestid}-load-more`}
                onClick={handleLoadMore}
                disabled={loadingList}
              >
                <span className="text-litlingo-primary">
                  {loadingList ? 'Loading…' : 'Load more'}
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestigationSelect;
